import React, { useCallback, useState } from 'react';
import { Redirect } from 'react-router';
import { connect } from 'react-redux';

import { Button, Checkbox, Form, Input, ConfigProvider } from 'antd-v5';
import { gAlch } from './helpers/Alch';
import { LoginHandler } from './helpers/loginApi';
import { handleLoginRememberMe } from './helpers';

// Official Clientbook Logo URL
const CLIENTBOOK_LOGO_URL = 'https://cdn.prod.website-files.com/5f15c00e4ffd49ccdceda3e2/66f5272d6da01e9c089a0165_Clientbook%20logo%202.svg';

const LoginLayout = ({ redirectTo, postLoginCallback = () => { } }) => {
  const [form] = Form.useForm();
  const [errorMsg, setErrorMsg] = useState('');
  const [redirect, setRedirect] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const currentYear = new Date().getFullYear();

  // Light theme for Ant Design - aligned with Clientbook brand guidelines
  const lightTheme = {
    token: {
      colorPrimary: '#3AA4FC',  // Clientbook Blue
      colorLink: '#3AA4FC',
      colorLinkHover: '#2B8FE0',
      borderRadius: 8,
      fontFamily: 'Poppins, sans-serif',
      colorText: '#1A2520',  // Clientbook Dark
      colorTextSecondary: '#6B7280',
    },
    components: {
      Input: {
        colorBgContainer: '#FFFFFF',
        colorBorder: '#E5E7EB',
        colorPrimaryHover: '#3AA4FC',
        activeBorderColor: '#3AA4FC',
        hoverBorderColor: '#D1D5DB',
        borderRadius: 8,
        colorText: '#1A2520',
        colorTextPlaceholder: '#9CA3AF',
        activeShadow: '0 0 0 3px rgba(58, 164, 252, 0.12)',
      },
      Button: {
        colorPrimary: '#3AA4FC',
        colorPrimaryHover: '#2B8FE0',
        borderRadius: 8,
      },
      Checkbox: {
        colorPrimary: '#3AA4FC',
        colorPrimaryHover: '#2B8FE0',
        colorText: '#4B5563',
        colorBgContainer: '#FFFFFF',
        colorBorder: '#D1D5DB',
      },
    },
  };

  const onFinish = useCallback(async values => {
    setIsButtonDisabled(true);
    try {
      const { email, password, remember } = values;
      const tkNa = await gAlch({ emailText: email, pwText: password });
      if (!tkNa?.tKn) {
        setIsButtonDisabled(false);
        setErrorMsg('Login error');
        return;
      }
      const newLogin = new LoginHandler('basic', null);
      const authResp = await newLogin.loginHandler({ tKn: tkNa?.tKn, ...tkNa?.auth }, {});
      if (authResp?.error) {
        const err = authResp.error;
        if (typeof err === 'string') {
          setErrorMsg(err);
        } else {
          setErrorMsg('Login error');
        }
        if (err.response && err.response.data && err.response.data.message) {
          if (typeof err.response.data.message !== 'string') {
            console.error({ error: JSON.stringify(err.response.data.message) });
          } else {
            console.error({ error: err.response.data.message.split('"').join('') });
          }
        } else {
          console.error({ error: 'Server error' });
        }
      }
      if (authResp?.merchantId) {
        const { token } = authResp;
        handleLoginRememberMe(remember, token, authResp);
        await postLoginCallback();
        setRedirect(true);
      }
    } catch (e) {
      setIsButtonDisabled(false);
      setErrorMsg('Login error');
      console.error(e);
    } finally {
      setIsButtonDisabled(false);
    }
  }, [postLoginCallback]);

  return (
    <div className="login-page-light">
      {/* Centered card container */}
      <div className="login-card">
        {/* Logo */}
        <img
          src={CLIENTBOOK_LOGO_URL}
          alt="Clientbook"
          className="login-card-logo"
        />

        {/* Header text */}
        <h1 className="login-card-title">Sign in</h1>
        <p className="login-card-subtitle">Access your dashboard</p>

        <Form
          className="login-card-form"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          disabled={isButtonDisabled}
          initialValues={{
            remember: true,
          }}
          validateTrigger="onSubmit"
        >
          <ConfigProvider theme={lightTheme}>
            {errorMsg && <div className="login-card-error">{errorMsg}</div>}

            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input
                size="large"
                placeholder="Email address"
                className="login-card-input"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter your password' }]}
            >
              <Input.Password
                size="large"
                placeholder="Password"
                className="login-card-input"
                autoComplete="current-password"
              />
            </Form.Item>

            <div className="login-card-options">
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>Remember me</Checkbox>
              </Form.Item>
              <a href="/Login/reset-password" className="login-card-forgot">
                Forgot password?
              </a>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              className="login-card-button"
              size="large"
              loading={isButtonDisabled}
              disabled={isButtonDisabled}
            >
              Sign in
            </Button>
          </ConfigProvider>
        </Form>
      </div>

      {/* Footer */}
      <div className="login-page-footer">
        <span>© {currentYear} Clientbook</span>
        <span className="login-footer-dot">·</span>
        <a href="https://help.clientbook.com/en/" target="_blank" rel="noopener noreferrer">
          Help
        </a>
        <span className="login-footer-dot">·</span>
        <a href="https://clientbook.com/privacy" target="_blank" rel="noopener noreferrer">
          Privacy
        </a>
      </div>

      {redirect ? <Redirect to={redirectTo || '/'} /> : null}
    </div>
  );
};

const mapStateToProps = state => state;
const mapDispatchToProps = dispatch => ({
  setAuth(account, token) {
    dispatch({
      type: 'SET_AUTH',
      payload: { account, token },
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginLayout);
